import { useEffect } from "react";
import { PageShell, GoldButton } from "@/components/ds";
import { DoreCalculator } from "@/components/ds/DoreCalculator";
import { Reveal } from "@/components/site/Reveal";
import { setPageMeta, injectJsonLd } from "@/lib/seo";
import { Link } from "react-router-dom";

const DoreTool = () => {
  useEffect(() => {
    setPageMeta({
      title: "Doré Net-Value Calculator | Zorem",
      description: "Estimate the net value of gold doré: gross weight, XRF purity, payable rate, and live XAU/USD spot. Built for African doré corridors and Dubai settlement.",
      path: "/tools/dore-calculator",
    });
    injectJsonLd("tool-jsonld", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Doré Net-Value Estimator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          provider: { "@type": "Organization", name: "Zorem Commodities LLC" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://zoremcommodities.com/" },
            { "@type": "ListItem", position: 2, name: "Tools", item: "https://zoremcommodities.com/tools/dore-calculator" },
            { "@type": "ListItem", position: 3, name: "Doré Net-Value Calculator" },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "What is a doré payable rate?", acceptedAnswer: { "@type": "Answer", text: "The percentage of verified fine gold content a refinery credits to the seller after refining losses, treatment charges, and margin — typically 92–97%." } },
            { "@type": "Question", name: "Which assay number should I use?", acceptedAnswer: { "@type": "Answer", text: "Use your XRF screening result for estimation (e.g., Au 95.89%). Final settlement always follows the destination refinery's fire assay." } },
            { "@type": "Question", name: "Is the spot price live?", acceptedAnswer: { "@type": "Answer", text: "The estimator loads the current XAU/USD spot automatically; you can override it. Display values are indicative, not executable rates." } },
          ],
        },
      ],
    });
    return () => document.getElementById("tool-jsonld")?.remove();
  }, []);

  return (
    <div data-testid="dore-tool-page">
      <PageShell eyebrow="Desk Tool" title="Doré net-value calculator.">
        <div className="grid lg:grid-cols-2 gap-12 items-start -mt-4">
          <Reveal>
            <DoreCalculator />
            <div className="mt-8 flex flex-wrap gap-4">
              <GoldButton to="/insights/dore-payable-rates-explained" variant="ghost" testid="tool-cta-article">How payable rates work</GoldButton>
              <GoldButton to="/mandate" testid="tool-cta-mandate">Submit a Mandate</GoldButton>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-xl text-[#E8C85A] tracking-wide mb-4">What factors influence a doré payable rate?</h2>
            <div className="space-y-4 text-[15px] text-zinc-400 leading-relaxed">
              <p>
                The payable rate is the refinery's truth serum. It prices four things at once:
                verified purity (your XRF reading, confirmed by fire assay), the silver and base-metal
                content that must be separated, the refinery's treatment charges, and how much the
                refinery trusts the corridor the metal arrived through.
              </p>
              <p>
                Sellers improve their payable rate by improving what the refinery sees: independent
                assay at origin, complete export documentation, and delivery through recognized
                logistics. A documented lot from a known corridor negotiates from strength; an
                anonymous lot negotiates from hope.
              </p>
              <p>
                Read the full mechanics in{" "}
                <Link to="/insights/dore-payable-rates-explained" className="text-[#D4AF37] hover:text-[#E8C85A] transition-colors underline underline-offset-4">
                  Doré payable rates explained
                </Link>
                , or verify your own material through a{" "}
                <Link to="/process" className="text-[#D4AF37] hover:text-[#E8C85A] transition-colors underline underline-offset-4">
                  structured trial lift
                </Link>
                .
              </p>
              <p>
                How to use the estimator: enter gross bar weight in kilograms, the XRF purity from
                your screening report (our corridor benchmark is 95.89% Au), the payable rate your
                refinery or buyer is offering, and — if you want a scenario other than today's —
                override the live XAU/USD spot. The estimator returns fine gold content, payable
                ounces, and an estimated net value. It is an indicative desk tool, not a settlement
                engine: final numbers always follow the destination refinery's fire assay and the
                written instrument terms agreed between verified counterparties.
              </p>
            </div>
          </Reveal>
        </div>
      </PageShell>
    </div>
  );
};

export default DoreTool;
